/**
 * useJourneyDocuments Hook
 * ========================
 * 
 * Hook React per accedere ai documenti del journey.
 * Fase 1: Solo consultazione.
 * 
 * DOCUMENT STATUS (user-facing):
 * - not_available: Non ancora disponibile
 * - available: Disponibile per download
 * - updated: Aggiornato (nuova versione)
 * - replaced: Sostituito
 * - cancelled: Annullato
 * 
 * DOCUMENT TYPES:
 * - voucher: Voucher per fornitore
 * - booking_summary: Riepilogo prenotazione
 * - ticket: Biglietto
 * - info_sheet: Scheda informativa
 * - receipt_placeholder: Placeholder ricevuta
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== STATUS CONFIGURATION ==============

export const DOCUMENT_STATUS = {
  not_available: {
    key: 'not_available',
    label: 'Non disponibile',
    color: 'bg-gray-100 text-gray-500',
    showDownload: false
  },
  available: {
    key: 'available',
    label: 'Disponibile',
    color: 'bg-green-50 text-green-700',
    showDownload: true
  },
  updated: {
    key: 'updated',
    label: 'Aggiornato',
    color: 'bg-blue-50 text-blue-700',
    showDownload: true
  },
  replaced: {
    key: 'replaced',
    label: 'Sostituito',
    color: 'bg-amber-50 text-amber-700',
    showDownload: false
  },
  cancelled: {
    key: 'cancelled',
    label: 'Annullato',
    color: 'bg-red-50 text-red-600',
    showDownload: false
  }
};

// Delivery status for customer-facing display
export const DELIVERY_STATUS = {
  not_sent: {
    key: 'not_sent',
    label: null, // No badge shown
    color: ''
  },
  sent: {
    key: 'sent',
    label: 'Inviato via email',
    color: 'text-blue-600'
  },
  delivered_best_effort: {
    key: 'delivered_best_effort',
    label: 'Inviato via email',
    color: 'text-blue-600'
  }
};

export const DOCUMENT_TYPES = {
  voucher: { key: 'voucher', label: 'Voucher', icon: 'Ticket' },
  booking_summary: { key: 'booking_summary', label: 'Riepilogo prenotazione', icon: 'FileText' },
  ticket: { key: 'ticket', label: 'Biglietto', icon: 'Ticket' },
  info_sheet: { key: 'info_sheet', label: 'Scheda informativa', icon: 'Info' },
  receipt_placeholder: { key: 'receipt_placeholder', label: 'Ricevuta', icon: 'Receipt' }
};

// ============== HOOK ==============

/**
 * useJourneyDocuments - Hook per accedere ai documenti del journey
 * 
 * @param {string} code - Codice del viaggio
 * @returns {Object} - Documents data e helpers
 */
export function useJourneyDocuments(code) {
  const [documentsData, setDocumentsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load documents
  const loadDocuments = useCallback(async () => {
    if (!code) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/journey/${code}/documents`);
      
      if (!res.ok) {
        if (res.status === 404) {
          setError('Viaggio non trovato');
        } else {
          setError('Errore nel caricamento documenti');
        }
        setDocumentsData(null);
        return;
      }
      
      const data = await res.json();
      setDocumentsData(data);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setError('Errore di connessione');
      setDocumentsData(null);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // ============== DERIVED DATA ==============

  // All documents
  const documents = useMemo(() => {
    return documentsData?.documents || [];
  }, [documentsData]);

  // Available documents only
  const availableDocuments = useMemo(() => {
    return documents.filter(doc => 
      doc.status === 'available' || doc.status === 'updated'
    );
  }, [documents]);

  // Documents by item (for timeline badges)
  const byItem = useMemo(() => {
    return documentsData?.by_item || {};
  }, [documentsData]);

  // Summary counts
  const totalCount = documentsData?.total_count || 0;
  const availableCount = documentsData?.available_count || 0;

  // ============== HELPERS ==============

  // Get status config for a document
  const getDocumentStatus = useCallback((status) => {
    return DOCUMENT_STATUS[status] || DOCUMENT_STATUS.not_available;
  }, []);

  // Get type config for a document
  const getDocumentType = useCallback((type) => {
    return DOCUMENT_TYPES[type] || DOCUMENT_TYPES.info_sheet;
  }, []);

  // Get delivery status config for a document
  const getDeliveryStatus = useCallback((deliveryStatus) => {
    return DELIVERY_STATUS[deliveryStatus] || DELIVERY_STATUS.not_sent;
  }, []);

  // Get documents for a specific item
  const getDocumentsForItem = useCallback((itemId) => {
    return documents.filter(doc => doc.item_id === itemId);
  }, [documents]);

  // Get general documents (not linked to item)
  const getGeneralDocuments = useCallback(() => {
    return documents.filter(doc => !doc.item_id);
  }, [documents]);

  // Get item summary (for timeline badge)
  const getItemSummary = useCallback((itemId) => {
    return byItem[itemId] || null;
  }, [byItem]);

  // Check if item has available documents
  const itemHasDocuments = useCallback((itemId) => {
    const summary = byItem[itemId];
    return summary && summary.available_documents > 0;
  }, [byItem]);

  // Check if item has voucher
  const itemHasVoucher = useCallback((itemId) => {
    const summary = byItem[itemId];
    return summary && summary.has_voucher;
  }, [byItem]);

  return {
    // Raw data
    documentsData,
    loading,
    error,
    
    // Documents
    documents,
    availableDocuments,
    byItem,
    
    // Counts
    totalCount,
    availableCount,
    
    // Helpers
    getDocumentStatus,
    getDocumentType,
    getDeliveryStatus,
    getDocumentsForItem,
    getGeneralDocuments,
    getItemSummary,
    itemHasDocuments,
    itemHasVoucher,
    
    // Actions
    reload: loadDocuments
  };
}

export default useJourneyDocuments;
